import React, { createContext, useContext, useState, ReactNode, useRef } from "react";
import { toFilterDateString } from "utils/filterDates";

interface FilterState {
  // Canonical form is a "YYYY-MM-DD" string — see utils/filterDates.
  checkInDate: string | null;
  checkOutDate: string | null;
  guestNumber: number;
  number_of_children:number,
  number_of_infants:number,
  number_of_pets:number,
  location: string | null;
  // A known landmark/neighbourhood the guest searched "near" (one of the names
  // in NEIGHBOURHOODS), or null. Used to focus the map and sort results by
  // closeness. Set by the AI search, reset when a new search isn't near anything.
  nearbyArea: string | null;
  // A free-form "near <place>" target with coordinates, for anywhere that isn't
  // in the hardcoded NEIGHBOURHOODS table (e.g. "near the Musée d'Orsay"). The
  // API validates the coordinates fall inside a served city before returning
  // them. nearbyArea's own coordinates win when both are set — see the effect
  // in SectionGridHasMap.
  nearbyPlace: { name: string; lat: number; lng: number } | null;
  handleSubmit: boolean |false
}

interface AppState {
  city: string | null;
  user: string | null;
  totalAmout:number | null;
  base_price:number | null;
  setBasePrice:any;
  setTotalAmount: (user: number | null) => void;
  isAuthenticated: boolean;
  setUser: (user: string | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setCity: (user: string | null) => void;
  filterState: FilterState | null;
  setFilterState: (filterState: FilterState) => void;
  updateFilterState:any;
  updateFilterValue:any;
  handleReserveClick:any;
  paymentSectionRef:any;
  filterValues: FilterValues; 
  setFilterValues: (values: FilterValues) => void; 
  resetFilter:any;
  setResetFilter:any;
  locationCounts: Record<string, number>;
  setLocationCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  filteredCount: number;
  setFilteredCount: React.Dispatch<React.SetStateAction<number>>;
  showMoreFilters: boolean;
  setShowMoreFilters: React.Dispatch<React.SetStateAction<boolean>>;
  // The AI search bar's raw text lives here so "Clear all" (in MoreFiltersPanel)
  // can reset it alongside the filters it produced — the search box and the
  // applied filters stay in sync.
  aiQuery: string;
  setAiQuery: React.Dispatch<React.SetStateAction<string>>;
}

interface FilterValues {
  toilets: number;
  bedrooms: number;
  bathrooms: number;
  propertyType: string[];
  rangePrices: string[];
  propertyRules: string[];
  amenities: string[];
  safetyMeasures: string[];
  stayTypes: string[];
}

const AppContext = createContext<AppState | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const getStoredFilterState = (): FilterState => {
    const defaults: FilterState = {
      checkInDate: null,
      checkOutDate: null,
      guestNumber: 1,
      number_of_children: 0,
      number_of_infants: 0,
      number_of_pets: 0,
      location: "Paris",
      nearbyArea: null,
      nearbyPlace: null,
      handleSubmit: false,
    };
    const storedFilters = sessionStorage.getItem("filterData");
    if (!storedFilters) return defaults;

    const parsed = JSON.parse(storedFilters);
    // Older sessions may hold a serialised Date or an "Invalid date" string —
    // normalise on the way in so nothing downstream has to cope with it.
    return {
      ...defaults,
      ...parsed,
      checkInDate: toFilterDateString(parsed.checkInDate),
      checkOutDate: toFilterDateString(parsed.checkOutDate),
    };
  };

  const getStoredFilterValue = (): FilterValues => {
    const defaults: FilterValues = {
      toilets: 0,
      bedrooms: 0,
      bathrooms: 0,
      propertyType: [],
      rangePrices: [0, 1000] as unknown as string[],
      propertyRules: [],
      amenities: [],
      safetyMeasures: [],
      stayTypes: [],
    };
    const storedFilters = sessionStorage.getItem("filterValues");
    // Merge over defaults so keys added later aren't missing from an older stored blob
    return storedFilters
      ? { ...defaults, ...JSON.parse(storedFilters) }
      : defaults;
  };

  const [user, setUser] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>('Paris');
  const [resetFilter, setResetFilter] = useState(false);
  const [locationCounts, setLocationCounts] = useState<Record<string, number>>({});
  const [filteredCount, setFilteredCount] = useState<number>(0);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [aiQuery, setAiQuery] = useState("");

  const [totalAmout, setTotalAmount] = useState<number | null>(0);
    const [base_price, setBasePrice] = useState<number | null>(0);


  const [isAuthenticated, setAuthenticated] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>(getStoredFilterState());
  const [filterValues, setFilterValues] = useState<FilterValues>(getStoredFilterValue());

  const updateFilterState = (key: keyof FilterState, value: any) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const updateFilterValue = (key: keyof FilterState, value: any) => {
    setFilterValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const paymentSectionRef = useRef<HTMLDivElement | null>(null);

  const handleReserveClick = () => {
    paymentSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  return (
    <AppContext.Provider value={{ handleReserveClick,paymentSectionRef, totalAmout, base_price,setBasePrice,
    setTotalAmount, city, setCity, user, isAuthenticated, setUser, setAuthenticated,
    filterState, setFilterState, updateFilterState, filterValues, setFilterValues, updateFilterValue, setResetFilter, resetFilter, locationCounts, setLocationCounts, filteredCount, setFilteredCount, showMoreFilters, setShowMoreFilters, aiQuery, setAiQuery }}>
      {children}
    </AppContext.Provider>
  );
};


export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};